import React, { useEffect, useState } from "react";
import { Calculator, Sparkles, X } from "lucide-react";
import { api, formatEUR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const VAT = 1.21;
const Toggle = ({ value, onChange, id }) => (
  <div className="inline-flex rounded-md border border-border overflow-hidden text-[11px]" data-testid={id}>
    {[["excl", false], ["incl", true]].map(([k, v]) => <button key={k} type="button" onClick={() => onChange(v)} className={`px-2 py-1 ${value === v ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground"}`} data-testid={`${id}-${k}`}>{k}. btw</button>)}
  </div>
);

/* Snelle prijsrekenmachine in de bovenbalk: naam + inkoopprijs (incl./excl.) [+ marktprijs] → verkoopprijs excl./incl. */
export function QuickPriceCalc() {
  const { hasPermission, user } = useAuth();
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(""); const [cost, setCost] = useState(""); const [costIncl, setCostIncl] = useState(false);
  const [ref, setRef] = useState(""); const [refIncl, setRefIncl] = useState(true);
  const [cat, setCat] = useState(null); const [res, setRes] = useState(null);
  const allowed = user?.role === "owner" || user?.role === "super_admin" || hasPermission("prices.inventory");

  useEffect(() => {
    if (!open || !name.trim()) { setCat(null); return; }
    const h = setTimeout(() => api.get("/pricing/guess", { params: { text: name } }).then((r) => setCat(r.data.category)).catch(() => setCat(null)), 300);
    return () => clearTimeout(h);
  }, [name, open]);

  useEffect(() => {
    const c = Number(cost) || 0;
    if (!open || c <= 0) { setRes(null); return; }
    const net = costIncl ? c / VAT : c;
    const h = setTimeout(() => api.post("/pricing/calculate", { net: net.toFixed(4), category_code: cat?.code || undefined, reference_price: ref || undefined, reference_incl_vat: refIncl }).then((r) => setRes(r.data)).catch(() => setRes(null)), 250);
    return () => clearTimeout(h);
  }, [cost, costIncl, cat?.code, ref, refIncl, open]);

  if (!allowed) return null;
  const reset = () => { setName(""); setCost(""); setRef(""); setRes(null); setCat(null); };
  const tag = res?.advice_basis?.startsWith("marktvloer") ? t("marketFloorApplied") : res?.advice_basis?.startsWith("marktplafond") ? t("marketCapApplied") : null;
  return (
    <>
      <Button variant="ghost" size="icon" onClick={() => setOpen(true)} title={t("quickCalc")} data-testid="quick-calc-btn"><Calculator className="h-5 w-5" /></Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="quick-calc-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Calculator className="h-5 w-5 text-primary" />{t("quickCalc")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={t("quickCalcNamePh")} className="h-11 text-base" data-testid="quick-calc-name" />
              <div className="text-[11px] mt-1 min-h-[16px]" data-testid="quick-calc-category">{cat ? <span className="text-primary inline-flex items-center gap-1"><Sparkles className="h-3 w-3" />{cat.code} · {cat.name} ({cat.profile})</span> : name ? <span className="text-muted-foreground">{t("quickCalcNoCat")}</span> : null}</div>
            </div>
            <div className="flex items-center gap-2">
              <Input type="number" step="0.01" min="0" inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} placeholder={t("costPrice")} className="h-11 text-base font-mono flex-1" data-testid="quick-calc-cost" />
              <Toggle value={costIncl} onChange={setCostIncl} id="quick-calc-cost-vat" />
            </div>
            <div className="flex items-center gap-2">
              <Input type="number" step="0.01" min="0" inputMode="decimal" value={ref} onChange={(e) => setRef(e.target.value)} placeholder={t("marketPriceBE")} className="h-11 text-base font-mono flex-1" data-testid="quick-calc-ref" />
              <Toggle value={refIncl} onChange={setRefIncl} id="quick-calc-ref-vat" />
            </div>
            <div className="rounded-xl border border-border bg-muted/40 p-4" data-testid="quick-calc-result">
              {res?.advice ? (
                <>
                  <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">{t("sellingPrice")}</div>
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <span className="font-display text-3xl font-bold tabular-nums" data-testid="quick-calc-excl">{formatEUR(res.advice)}</span><span className="text-xs text-muted-foreground">excl. btw</span>
                    <span className="font-mono text-xl font-semibold tabular-nums" data-testid="quick-calc-incl">{formatEUR(res.advice_incl)}</span><span className="text-xs text-muted-foreground">incl. btw</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1">{t("costPrice")} {formatEUR(res.cost)} excl. · {t("markup")} {res.markup_pct}% · {t("margin")} {res.margin_pct}%{tag && <span className="text-primary font-semibold"> · {tag}</span>}</div>
                  {res.cost_too_high && <div className="text-[11px] text-rose-700 font-semibold mt-1" data-testid="quick-calc-cost-too-high">{t("costTooHigh")}</div>}
                </>
              ) : <div className="text-sm text-muted-foreground">{t("quickCalcHint")}</div>}
            </div>
            <div className="flex justify-between"><Button variant="ghost" size="sm" onClick={reset} data-testid="quick-calc-reset"><X className="h-4 w-4 mr-1" />{t("clear")}</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
